import { useState } from 'react';

import { useTrackEvent } from '@/lib/posthog/hooks/useTrackEvent';
import { startSlackLogin } from '@/modules/auth/slackSso';
import {
  authButtonClickedEvents,
  type AuthIntent,
} from '@/modules/auth/types/externalLogin';
import { SlackLogo } from '@/modules/signup-flow/components/slackIntegration/SlackLogo';
import { Button } from '@/modules/ui/components/button/Button';

interface SlackLoginProps {
  returnTo?: string;
  /** True once the Terms Notice beside the button shows its link. */
  acceptTerms: boolean;
  /** Whether the callback reports `userSignedUp` or `userSignedIn`. */
  intent: AuthIntent;
  onError: (error?: Error) => void;
}

/**
 * Slack's "Sign in with Slack" button: white with a light border, the
 * four-colour mark and dark bold label. It signs the user in only; connecting
 * Finlay's bot to the workspace stays a separate step.
 */
export const SlackLogin = ({
  returnTo,
  acceptTerms,
  intent,
  onError,
}: SlackLoginProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const trackEvent = useTrackEvent();

  const handleLogin = async () => {
    try {
      setIsLoading(true);
      trackEvent(authButtonClickedEvents[intent], { authMethod: 'Slack' });
      trackEvent('slackSSO', { stage: 'started' });

      await startSlackLogin(returnTo, acceptTerms, intent);
    } catch (error) {
      setIsLoading(false);
      onError(error instanceof Error ? error : undefined);
    }
  };

  return (
    <Button
      variant="outline"
      loading={isLoading}
      onClick={handleLogin}
      // Matches Google's large button beside it: 40px tall, 4px corners, the
      // icon at the left edge and the label centred.
      className="relative h-10 w-full rounded-[4px] border-[#dddddd] font-semibold text-[#1d1c1d] active:scale-[0.98]"
    >
      {!isLoading && <SlackLogo className="absolute left-3 size-4" />}
      {intent === 'signUp' ? 'Sign up with Slack' : 'Continue with Slack'}
    </Button>
  );
};
